import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { PersonaAvatar } from "@/components/persona-identity";
import { Chip } from "@/components/ui/chip";
import { cn } from "@/lib/utils";

// ── Types ─────────────────────────────────────────────────────

export type PersonaVariant = "full" | "compact" | "pill" | "inline";

export interface PersonaRowData {
  id: string;
  name: string;
  role?: string | null;
  brief?: string | null;
  context?: string | null;
  status?: "active" | "archived" | string | null;
}

export interface PersonaRowProps {
  persona: PersonaRowData;
  /** Layout variant: full (page index), compact (drawers/pickers), pill (cards/matrix), inline (prose/kicker) */
  variant?: PersonaVariant;
  /** Primary target link for the persona name */
  href?: string | null;
  /** Custom selection or click handler */
  onSelect?: (personaId: string) => void;
  /** Actions rendered on the right side */
  actions?: ReactNode;
  /** Trailing accessory element (e.g. status chip, action button) */
  trailing?: ReactNode;
  /** Leading element (e.g. checkbox or handle) */
  leading?: ReactNode;
  /** Contextual badge (used in pill variant) */
  badge?: ReactNode;
  /** Custom subtitle line for compact variant */
  subtitle?: ReactNode;
  /** Avatar size override */
  size?: "xs" | "sm" | "md" | "lg";
  /** Heading level for semantic outline */
  headingLevel?: "h2" | "h3" | "h4" | "span";
  className?: string;
}

// ── Component ────────────────────────────────────────────────

/**
 * PersonaRow (aliased as PersonaItem) — canonical visual representation of a persona.
 * Supports 4 variants: "full", "compact", "pill", and "inline".
 */
export function PersonaRow({
  persona,
  variant = "full",
  href,
  onSelect,
  actions,
  trailing,
  leading,
  badge,
  subtitle,
  size,
  headingLevel: Heading = "h3",
  className,
}: PersonaRowProps) {
  const preview = persona.brief || persona.context;

  // ── Variant: Inline (Finding provenance, citation cards, prose) ──
  if (variant === "inline") {
    const avatarSize = size ?? "xs";
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 font-medium text-foreground",
          className,
        )}
      >
        <PersonaAvatar name={persona.name} size={avatarSize} />
        {href ? (
          <Link
            to={href}
            onClick={(e) => {
              if (
                onSelect &&
                !e.metaKey &&
                !e.ctrlKey &&
                !e.shiftKey &&
                !e.altKey &&
                e.button === 0
              ) {
                e.preventDefault();
                onSelect(persona.id);
              }
            }}
            className="hover:underline underline-offset-4 decoration-border"
            title={persona.name}
          >
            {persona.name}
          </Link>
        ) : onSelect ? (
          <button
            type="button"
            onClick={() => onSelect(persona.id)}
            className="hover:underline underline-offset-4 decoration-border text-left cursor-pointer"
            title={persona.name}
          >
            {persona.name}
          </button>
        ) : (
          <span>{persona.name}</span>
        )}
        {persona.role && (
          <span className="text-xs text-muted-foreground font-normal">
            · {persona.role}
          </span>
        )}
      </span>
    );
  }

  // ── Variant: Pill (Scenario cards, tags, matrix cells) ────────
  if (variant === "pill") {
    const avatarSize = size ?? "xs";
    return (
      <div
        className={cn(
          "group inline-flex max-w-full items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/20 px-2.5 py-1 text-xs transition-colors hover:border-border hover:bg-muted/40",
          className,
        )}
      >
        <div className="flex min-w-0 items-center gap-1.5">
          {href ? (
            <Link
              to={href}
              onClick={(e) => {
                if (
                  onSelect &&
                  !e.metaKey &&
                  !e.ctrlKey &&
                  !e.shiftKey &&
                  !e.altKey &&
                  e.button === 0
                ) {
                  e.preventDefault();
                  onSelect(persona.id);
                }
              }}
              className="flex min-w-0 items-center gap-1.5 truncate font-medium text-foreground hover:underline"
              title={persona.name}
            >
              <PersonaAvatar name={persona.name} size={avatarSize} />
              <span className="truncate">{persona.name}</span>
            </Link>
          ) : onSelect ? (
            <button
              type="button"
              onClick={() => onSelect(persona.id)}
              className="flex min-w-0 items-center gap-1.5 truncate text-left font-medium text-foreground hover:underline cursor-pointer"
              title={persona.name}
            >
              <PersonaAvatar name={persona.name} size={avatarSize} />
              <span className="truncate">{persona.name}</span>
            </button>
          ) : (
            <div className="flex min-w-0 items-center gap-1.5">
              <PersonaAvatar name={persona.name} size={avatarSize} />
              <span
                className="truncate font-medium text-foreground"
                title={persona.name}
              >
                {persona.name}
              </span>
            </div>
          )}
        </div>
        {badge && <div className="shrink-0">{badge}</div>}
        {trailing && <div className="shrink-0">{trailing}</div>}
      </div>
    );
  }

  // ── Variant: Compact (Casting select, command palette, sidebars) ─
  if (variant === "compact") {
    const avatarSize = size ?? "sm";
    return (
      <div
        className={cn(
          "group flex min-w-0 flex-1 items-center justify-between gap-3 py-1",
          className,
        )}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          {leading}
          <PersonaAvatar name={persona.name} size={avatarSize} />
          <div className="min-w-0 flex-1 space-y-0.5">
            <Heading
              className="min-w-0 truncate text-sm font-semibold leading-tight text-foreground"
              title={persona.name}
            >
              {href ? (
                <Link
                  to={href}
                  aria-label={`View persona profile for ${persona.name}`}
                  onClick={(e) => {
                    if (
                      onSelect &&
                      !e.metaKey &&
                      !e.ctrlKey &&
                      !e.shiftKey &&
                      !e.altKey &&
                      e.button === 0
                    ) {
                      e.preventDefault();
                      onSelect(persona.id);
                    }
                  }}
                  className="hover:underline underline-offset-4 decoration-border focus-visible:outline-2 focus-visible:outline-ring rounded-xs"
                >
                  {persona.name}
                </Link>
              ) : onSelect ? (
                <button
                  type="button"
                  aria-label={`View persona profile for ${persona.name}`}
                  onClick={() => onSelect(persona.id)}
                  className="hover:underline underline-offset-4 decoration-border text-left cursor-pointer focus-visible:outline-2 focus-visible:outline-ring rounded-xs"
                >
                  {persona.name}
                </button>
              ) : (
                persona.name
              )}
            </Heading>
            {(subtitle || persona.role) && (
              <span className="text-xs text-muted-foreground block truncate">
                {subtitle ?? persona.role}
              </span>
            )}
          </div>
        </div>

        {(trailing || actions) && (
          <div className="flex shrink-0 items-center gap-2">
            {trailing}
            {actions}
          </div>
        )}
      </div>
    );
  }

  // ── Variant: Full (Personas index table / casting management) ─
  const avatarSize = size ?? "md";
  return (
    <div
      className={cn(
        "flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors",
        className,
      )}
    >
      {leading}
      <PersonaAvatar name={persona.name} size={avatarSize} />

      <div className="flex-1 min-w-0 lg:w-56 lg:flex-initial">
        <Heading className="text-sm font-semibold tracking-tight text-foreground block truncate">
          {href ? (
            <Link
              to={href}
              aria-label={`View persona profile for ${persona.name}`}
              onClick={(e) => {
                if (
                  onSelect &&
                  !e.metaKey &&
                  !e.ctrlKey &&
                  !e.shiftKey &&
                  !e.altKey &&
                  e.button === 0
                ) {
                  e.preventDefault();
                  onSelect(persona.id);
                }
              }}
              className="hover:underline underline-offset-4 decoration-border focus-visible:outline-2 focus-visible:outline-ring rounded-xs"
            >
              {persona.name}
            </Link>
          ) : onSelect ? (
            <button
              type="button"
              aria-label={`View persona profile for ${persona.name}`}
              onClick={() => onSelect(persona.id)}
              className="hover:underline underline-offset-4 decoration-border text-left cursor-pointer focus-visible:outline-2 focus-visible:outline-ring rounded-xs"
            >
              {persona.name}
            </button>
          ) : (
            persona.name
          )}
        </Heading>
        {persona.role && (
          <span className="text-xs text-muted-foreground block truncate">
            {persona.role}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0 hidden lg:block">
        {preview ? (
          <p
            className="line-clamp-2 text-xs text-muted-foreground leading-relaxed"
            title={preview}
          >
            {preview}
          </p>
        ) : (
          <span className="text-xs text-muted-foreground">
            No background recorded
          </span>
        )}
      </div>

      {persona.status && (
        <div className="w-16 lg:w-24 shrink-0 flex justify-center">
          {persona.status === "archived" ? (
            <Chip tone="muted">Archived</Chip>
          ) : (
            <Chip tone="helps">Active</Chip>
          )}
        </div>
      )}

      {(trailing || actions) && (
        <div className="w-10 lg:w-32 shrink-0 flex items-center justify-end gap-1.5">
          {trailing}
          {actions}
        </div>
      )}
    </div>
  );
}

export { PersonaRow as PersonaItem };
